import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, ExternalLink, Edit2, Trash2, Clock, X, Upload, LogOut, User, ChevronDown, ChevronUp, RotateCcw, SlidersHorizontal, Sun, Moon } from 'lucide-react';
import { useProjects } from '../contexts/ProjectsContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { resumableUpload, shouldUseResumableUpload } from '../lib/resumableUpload';
import { PDFDocument } from 'pdf-lib';
import { extractMetadataFromPDF, BidflowMetadata } from '../lib/bidflowMetadata';
import { saveAnnotationSnapshotV2 } from '../lib/annotationV2Persistence';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/HomePage.css';

function HomePage() {
  const navigate = useNavigate();
  const { projects: allProjects, updateProject, deleteProject } = useProjects();
  const { user, username, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [sortBy, setSortBy] = useState<'lastOpened' | 'name' | 'dateCreated'>('lastOpened');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showSortDropdown, setShowSortDropdown] = useState<boolean>(false);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [creatingProject, setCreatingProject] = useState<boolean>(false);
  const [newProjectName, setNewProjectName] = useState<string>('');
  const [newProjectFiles, setNewProjectFiles] = useState<File[]>([]);
  const [newSpecsFiles, setNewSpecsFiles] = useState<File[]>([]);
  const [isMergingPDFs, setIsMergingPDFs] = useState<boolean>(false);
  const [mergeProgress, setMergeProgress] = useState<number>(0);
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [blueprintFiles, setBlueprintFiles] = useState<any[]>([]);
  const [specsFilesForManager, setSpecsFilesForManager] = useState<any[]>([]);
  const [projectFilesCache, setProjectFilesCache] = useState<Record<string, { blueprints: any[]; specs: any[] }>>({});
  const [fileNameEdits, setFileNameEdits] = useState<Record<string, string>>({});
  const [pendingDrawingFiles, setPendingDrawingFiles] = useState<Array<{ file: File; tempId: string }>>([]);
  const [pendingDrawingDeletions, setPendingDrawingDeletions] = useState<Set<string>>(new Set());
  const [pendingSpecsFiles, setPendingSpecsFiles] = useState<Array<{ file: File; tempId: string }>>([]);
  const [pendingSpecsDeletions, setPendingSpecsDeletions] = useState<Set<string>>(new Set());
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [editingProjectName, setEditingProjectName] = useState<string>('');
  const [editingProjectNameId, setEditingProjectNameId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showStatusPicker, setShowStatusPicker] = useState<string | null>(null);
  
  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  // Drag and drop states
  const [isDraggingDrawing, setIsDraggingDrawing] = useState<boolean>(false);
  const [isDraggingSpecs, setIsDraggingSpecs] = useState<boolean>(false);
  const [isDraggingModalDrawing, setIsDraggingModalDrawing] = useState<boolean>(false);
  const [isDraggingModalSpecs, setIsDraggingModalSpecs] = useState<boolean>(false);

  useEffect(() => {
    if (!showStatusPicker) return;
    const handleClick = () => setShowStatusPicker(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showStatusPicker]);

  useEffect(() => {
    if (!showSortDropdown) return;
    const handleClick = () => setShowSortDropdown(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showSortDropdown]);

  // Capitalize function
  const capitalizeWords = (str: string) => {
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Merge multiple PDFs into one
  const mergePDFs = async (files: File[]): Promise<File> => {
    console.log(`📄 Merging ${files.length} PDF files...`);
    const mergedPdf = await PDFDocument.create();
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`   Processing ${i + 1}/${files.length}: ${file.name}`);
      
      // Update progress: processing phase
      const loadProgress = ((i / files.length) * 80);
      setMergeProgress(loadProgress);
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
      
      // Update progress after each file
      const processProgress = ((i + 1) / files.length) * 80;
      setMergeProgress(processProgress);
    }
    
    // Saving progress (final 20%)
    setMergeProgress(85);
    console.log('   Saving merged PDF...');
    const mergedPdfBytes = await mergedPdf.save();
    
    setMergeProgress(100);
    const mergedFile = new File([mergedPdfBytes], 'merged-drawings.pdf', { type: 'application/pdf' });
    console.log(`✅ Merged PDF created: ${(mergedFile.size / 1024 / 1024).toFixed(2)} MB`);
    return mergedFile;
  };

  const statusOptions = [
    { value: 'estimating', label: 'Estimating', color: 'var(--yellow-text-dark)', bgColor: '#fef3c7' },
    { value: 'in-review', label: 'In Review', color: 'var(--purple-text-dark)', bgColor: '#e9d5ff' },
    { value: 'completed', label: 'Completed', color: 'var(--green-text-dark)', bgColor: 'var(--green-tint-strong)' },
  ];

  const getStatusOption = (status: string) => {
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

const splitFileName = (fileName: string) => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) {
    return { base: fileName, ext: '' };
  }
  return {
    base: fileName.slice(0, lastDot),
    ext: fileName.slice(lastDot),
  };
};

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

const buildFileNameEditMap = (blueprints: any[] = [], specs: any[] = []) => {
  const edits: Record<string, string> = {};
  blueprints.forEach((file) => {
    const { base } = splitFileName(file.file_name);
    edits[file.id] = base;
  });
  specs.forEach((file) => {
    const { base } = splitFileName(file.file_name);
    edits[file.id] = base;
  });
  return edits;
};

const getEditedFileName = (fileId: string, originalName: string, edits: Record<string, string>) => {
  const { base, ext } = splitFileName(originalName);
  const editedBase = (edits[fileId] ?? base).trim() || base;
  return `${editedBase}${ext}`;
};

  const displayProjects = useMemo(() => {
    let filtered = allProjects;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = allProjects.filter(project =>
        project.name.toLowerCase().includes(query)
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortBy === 'dateCreated') {
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else {
        cmp = new Date(a.lastOpenedAt || a.updatedAt).getTime() - new Date(b.lastOpenedAt || b.updatedAt).getTime();
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [allProjects, searchQuery, sortBy, sortDirection]);

  const handleDelete = async (projectId: string, projectName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"?`)) return;

    try {
      // Delete from Supabase
      if (supabase) {
        // First get all files associated with this project
        const { data: files } = await supabase
          .from('estimation_files')
          .select('storage_path')
          .eq('estimation_id', projectId);

        // Delete files from storage
        if (files && files.length > 0) {
          const filePaths = files.map(f => f.storage_path);
          await supabase.storage
            .from('bucket')
            .remove(filePaths);
        }

        // Delete the estimation (this will cascade to estimation_files and other related tables)
        const { error } = await supabase
          .from('estimations')
          .delete()
          .eq('id', projectId);

        if (error) {
          console.error('Error deleting from Supabase:', error);
          alert('Failed to delete project');
          return;
        }
      }
      
      await deleteProject(projectId);
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      alert('Please provide a project name');
      return;
    }

    setCreatingProject(true);

    try {
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      // Check uploaded PDFs for embedded bidflow annotation metadata
      let detectedMetadata: BidflowMetadata | null = null;
      for (const pdfFile of newProjectFiles) {
        if (pdfFile.type === 'application/pdf' || pdfFile.name.toLowerCase().endsWith('.pdf')) {
          try {
            const bytes = await pdfFile.arrayBuffer();
            const meta = await extractMetadataFromPDF(bytes);
            if (meta) {
              console.log(`📦 Found bidflow metadata in ${pdfFile.name}: ${meta.projectKey.components.length} components, ${Object.values(meta.annotations).reduce((s, a) => s + a.length, 0)} annotations`);
              detectedMetadata = meta;
              break;
            }
          } catch (err) {
            console.warn(`Could not check ${pdfFile.name} for metadata:`, err);
          }
        }
      }

      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(7);

      // Create project in estimations table
      const { data: projectData, error: projectError } = await supabase
        .from('estimations')
        .insert({
          name: capitalizeWords(newProjectName.trim()),
          client_name: 'New Client',
          project_type: 'commercial',
          status: 'estimating',
          folder_id: '00000000-0000-0000-0000-000000000001',
          username: username,
          user_id: user?.id, // Required for RLS - links estimation to authenticated user
        })
        .select()
        .single();

      if (projectError) {
        throw projectError;
      }

      // Upload drawing PDF if provided (merge multiple files if needed)
      if (newProjectFiles.length > 0) {
        let fileToUpload: File;
        
        if (newProjectFiles.length > 1) {
          // Merge multiple PDFs into one
          setIsMergingPDFs(true);
          setMergeProgress(0);
          console.log(`🔄 Merging ${newProjectFiles.length} PDF files...`);
          fileToUpload = await mergePDFs(newProjectFiles);
          setIsMergingPDFs(false);
          setMergeProgress(0);
        } else {
          fileToUpload = newProjectFiles[0];
        }
        
        console.log(`🔍 DEBUG: Uploading blueprint ${fileToUpload.name}, size: ${(fileToUpload.size / 1024 / 1024).toFixed(2)} MB`);
        
        setIsUploading(true);
        setUploadingFileName(fileToUpload.name);
        setUploadProgress(0);
        
        const filePath = `blueprints/${timestamp}_${randomStr}.pdf`;
        let uploadSuccess = false;
        
        if (shouldUseResumableUpload(fileToUpload.size)) {
          console.log('📤 Using TUS resumable upload for large file...');
          const result = await resumableUpload({
            bucketName: 'bucket',
            filePath,
            file: fileToUpload,
            onProgress: (progress) => {
              setUploadProgress(progress.percentage);
              console.log(`   Upload progress: ${progress.percentage}%`);
            },
            onError: (error) => {
              console.error(`   Upload error:`, error);
              setIsUploading(false);
            }
          });
          uploadSuccess = result.success;
          if (!uploadSuccess && result.error) {
            setIsUploading(false);
            throw result.error;
          }
        } else {
          console.log('📤 Using standard upload for small file...');
          const { error: uploadError } = await supabase.storage
            .from('bucket')
            .upload(filePath, fileToUpload);

          if (uploadError) {
            setIsUploading(false);
            throw uploadError;
          }
          uploadSuccess = true;
        }
        
        setIsUploading(false);
        setUploadProgress(100);
        
        if (!uploadSuccess) {
          throw new Error('Upload failed');
      }

      // Add drawing file to estimation_files table
      const { data: fileData, error: fileError } = await supabase
        .from('estimation_files')
        .insert({
          estimation_id: projectData.id,
          file_name: fileToUpload.name,
          file_size: fileToUpload.size,
          file_type: 'application/pdf',
          storage_path: filePath,
          file_order: 0,
        })
        .select('id, file_name')
        .single();

      if (fileError) {
        throw fileError;
        }

      // Seed project from bidflow metadata if found
      if (detectedMetadata && fileData) {
        try {
          console.log('🌱 Seeding project from bidflow metadata...');

          // Merge reference images back into the project_key components
          const meta = detectedMetadata;
          const keyWithImages = {
            ...meta.projectKey,
            components: meta.projectKey.components.map(comp => ({
              ...comp,
              referenceImage: meta.referenceImages[comp.name] || comp.referenceImage || '',
              hasReferenceImage: !!(meta.referenceImages[comp.name] || comp.referenceImage),
            })),
          };

          // Set project_key (with reference images inline)
          const { error: keyError } = await supabase
            .from('estimations')
            .update({ project_key: keyWithImages })
            .eq('id', projectData.id);
          if (keyError) console.error('Error seeding project_key:', keyError);
          else console.log(`✅ Seeded project_key with ${keyWithImages.components.length} components`);

          // Insert annotations into v2 source-of-truth table
          const snapshotAnnotations = Object.entries(detectedMetadata.annotations).flatMap(([componentType, anns]) => {
            if (!Array.isArray(anns)) return [];
            return anns.map((ann: any) => ({
              ...ann,
              component_type: ann?.component_type || componentType,
            }));
          });

          if (snapshotAnnotations.length > 0) {
            await saveAnnotationSnapshotV2({
              supabase: supabase as any,
              estimationId: projectData.id,
              activeMode: fileData?.id ? 'draw' : 'estimate',
              currentDrawingFileId: fileData?.id || null,
              annotations: snapshotAnnotations,
            });
          }

          const totalAnns = Object.values(detectedMetadata.annotations).reduce((s, a) => s + a.length, 0);
          console.log(`✅ Seeded ${totalAnns} annotations`);
        } catch (seedErr) {
          console.error('Error seeding from metadata (project still created):', seedErr);
        }
      }
      }

      // Handle specs upload - upload all files separately (no merging)
      if (newSpecsFiles.length > 0) {
        console.log(`📤 Uploading ${newSpecsFiles.length} specification file(s)...`);
        
        const uploadPromises = newSpecsFiles.map(async (file, i) => {
          console.log(`🔍 DEBUG: Uploading spec ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
          
          setIsUploading(true);
          setUploadingFileName(file.name);
          setUploadProgress(0);
          
          const fileExt = file.name.split('.').pop();
          const specsPath = `specs/${timestamp}_${i}_${randomStr}.${fileExt}`;
          
          let uploadSuccess = false;
          
          if (shouldUseResumableUpload(file.size)) {
            console.log(`📤 Using TUS resumable upload for ${file.name}...`);
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath: specsPath,
              file,
              onProgress: (progress) => {
                setUploadProgress(progress.percentage);
                console.log(`   ${file.name} progress: ${progress.percentage}%`);
              },
              onError: (error) => {
                console.error(`   Upload error:`, error);
                setIsUploading(false);
              }
            });
            uploadSuccess = result.success;
          } else {
            console.log(`📤 Using standard upload for ${file.name}...`);
            const { error: uploadError } = await supabase.storage
              .from('bucket')
              .upload(specsPath, file);
            
            uploadSuccess = !uploadError;
            if (uploadError) {
              console.error(`❌ Standard upload error:`, uploadError);
            }
          }
          
          setIsUploading(false);

          if (uploadSuccess) {
            await supabase
              .from('estimation_specs')
              .insert({
                estimation_id: projectData.id,
                file_name: file.name,
                file_size: file.size,
                file_type: file.type,
                storage_path: specsPath,
                file_order: i,
              });
            console.log(`✅ Uploaded ${file.name}`);
          } else {
            console.error(`❌ Error uploading ${file.name}`);
          }
        });

        await Promise.all(uploadPromises);
        setIsUploading(false);
        console.log(`✅ Uploaded ${newSpecsFiles.length} specification file(s)`);
      }

      // Fire-and-forget: trigger page indexing in the background
      if (newProjectFiles.length > 0) {
        const blueprintPath = `blueprints/${timestamp}_${randomStr}.pdf`;
        const { data: indexUrlData } = supabase.storage.from('bucket').getPublicUrl(blueprintPath);
        if (indexUrlData?.publicUrl) {
          console.log('📋 Triggering page indexing (fire-and-forget)...');
          fetch('/api/index-drawing-pages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ estimationId: projectData.id, fileUri: indexUrlData.publicUrl })
          }).catch(err => console.warn('Page indexing fire-and-forget failed:', err));
        }
      }

      // Navigate to the new project
      navigate(`/estimation/${projectData.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project. Please try again.');
      setCreatingProject(false);
    }
  };

  const handleCancelCreate = () => {
    setNewProjectName('');
    setNewProjectFiles([]);
    setNewSpecsFiles([]);
    setShowCreateForm(false);
    setShowCreateModal(false);
    setIsMergingPDFs(false);
    setMergeProgress(0);
  };

  const handleSpecsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      // Check for duplicates
      const existingNames = newSpecsFiles.map(f => f.name.toLowerCase());
      const duplicates: string[] = [];
      const uniqueFiles = newFiles.filter(file => {
        if (existingNames.includes(file.name.toLowerCase())) {
          duplicates.push(file.name);
          return false;
        }
        return true;
      });
      
      if (duplicates.length > 0) {
        alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
      }
      
      setNewSpecsFiles(prev => [...prev, ...uniqueFiles]);
    }
  };

  const removeSpecsFile = (index: number) => {
    setNewSpecsFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeDrawingFile = (index: number) => {
    setNewProjectFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fetchProjectFiles = async (projectId: string) => {
    if (!supabase) return { blueprints: [], specs: [] };
    const [bpResult, specsResult] = await Promise.all([
      supabase
        .from('estimation_files')
        .select('*')
        .eq('estimation_id', projectId)
        .order('file_order', { ascending: true }),
      supabase
        .from('estimation_specs')
        .select('*')
        .eq('estimation_id', projectId)
        .order('file_order', { ascending: true })
    ]);

    return {
      blueprints: bpResult.data || [],
      specs: specsResult.data || []
    };
  };

  const handleOpenProject = async (projectId: string) => {
    try {
      if (supabase) {
        await supabase
          .from('estimations')
          .update({ last_opened_at: new Date().toISOString() })
          .eq('id', projectId);
      }
    } catch (error) {
      console.error('Error updating last_opened_at:', error);
    }
    navigate(`/estimation/${projectId}`);
    setSearchQuery('');
  };

  useEffect(() => {
    if (!supabase || displayProjects.length === 0) return;

    const preloadFiles = async () => {
      const projectsToLoad = displayProjects.filter(
        (project) => !projectFilesCache[project.id]
      );

      if (projectsToLoad.length === 0) return;

      try {
        const results = await Promise.all(
          projectsToLoad.map(async (project) => {
            const files = await fetchProjectFiles(project.id);
            return { projectId: project.id, ...files };
          })
        );

        setProjectFilesCache((prev) => {
          const updated = { ...prev };
          results.forEach(({ projectId, blueprints, specs }) => {
            updated[projectId] = { blueprints, specs };
          });
          return updated;
        });
      } catch (error) {
        console.error('Error preloading project files:', error);
      }
    };

    preloadFiles();
  }, [displayProjects, supabase, projectFilesCache]);

  // Toggle expand/collapse for project editing
  const handleToggleExpand = async (projectId: string) => {
    if (expandedProjectId === projectId) {
      // Collapse
      setExpandedProjectId(null);
      setBlueprintFiles([]);
      setSpecsFilesForManager([]);
      setEditingProjectNameId(null);
      setEditingProjectName('');
      setFileNameEdits({});
      setPendingDrawingFiles([]);
      setPendingDrawingDeletions(new Set());
      setPendingSpecsFiles([]);
      setPendingSpecsDeletions(new Set());
      setIsSavingChanges(false);
    } else {
      // Expand using cached data
      let cacheEntry = projectFilesCache[projectId];

      // Fallback fetch if not cached yet
      if (!cacheEntry) {
        const files = await fetchProjectFiles(projectId);
        cacheEntry = files;
        setProjectFilesCache((prev) => ({
          ...prev,
          [projectId]: files,
        }));
      }

      const bpList = cacheEntry.blueprints || [];
      const specsList = cacheEntry.specs || [];
      setBlueprintFiles(bpList);
      setSpecsFilesForManager(specsList);
      setEditingProjectNameId(null);
      setPendingDrawingFiles([]);
      setPendingDrawingDeletions(new Set());
      setPendingSpecsFiles([]);
      setPendingSpecsDeletions(new Set());
      setFileNameEdits(buildFileNameEditMap(bpList, specsList));
      setIsSavingChanges(false);
      setExpandedProjectId(projectId);
    }
  };

  // Update project name
  const handleUpdateProjectName = async (projectId: string) => {
    if (!editingProjectName.trim()) return;

    try {
      if (!supabase) return;
      
      const { error } = await supabase
        .from('estimations')
        .update({ name: editingProjectName.trim() })
        .eq('id', projectId);

      if (error) throw error;
      
      await updateProject(projectId, { name: editingProjectName.trim() });
      setEditingProjectName(editingProjectName.trim());
      
      // Refresh project list
      window.dispatchEvent(new Event('refetch-projects'));
    } catch (error) {
      console.error('Error updating project name:', error);
      alert('Failed to update project name');
    }
  };

  // Add drawing files (pending)
  const handleAddDrawingFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const uploads = Array.from(e.target.files).map((file, index) => {
      const tempId = `pending-drawing-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingDrawingFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
    const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  const handleRemovePendingDrawingFile = (tempId: string) => {
    setPendingDrawingFiles((prev) => prev.filter((entry) => entry.tempId !== tempId));
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      delete updated[tempId];
      return updated;
    });
  };

  const handleToggleDeleteDrawingFile = (fileId: string) => {
    setPendingDrawingDeletions((prev) => {
      const updated = new Set(prev);
      if (updated.has(fileId)) {
        updated.delete(fileId);
      } else {
        updated.add(fileId);
      }
      return updated;
    });
  };

  // Add specs files
  const handleAddSpecsFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const uploads = Array.from(e.target.files).map((file, index) => {
      const tempId = `pending-spec-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingSpecsFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
        const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  // Delete specs file
  const hasPendingFileChanges = () => {
    const drawingsRenamed = blueprintFiles.some(
      (file) =>
        !pendingDrawingDeletions.has(file.id) &&
        getEditedFileName(file.id, file.file_name, fileNameEdits) !== file.file_name
    );
    const specsRenamed = specsFilesForManager.some(
      (file) =>
        !pendingSpecsDeletions.has(file.id) &&
        getEditedFileName(file.id, file.file_name, fileNameEdits) !== file.file_name
    );

    return (
      pendingDrawingFiles.length > 0 ||
      pendingDrawingDeletions.size > 0 ||
      drawingsRenamed ||
      pendingSpecsFiles.length > 0 ||
      pendingSpecsDeletions.size > 0 ||
      specsRenamed
    );
  };

  const handleCancelFileChanges = () => {
    if (isSavingChanges) return; // Don't allow cancel while saving
    
    // Close the dropdown
    setExpandedProjectId(null);
    setBlueprintFiles([]);
    setSpecsFilesForManager([]);
    setEditingProjectNameId(null);
    setEditingProjectName('');
    setFileNameEdits({});
    setPendingDrawingFiles([]);
    setPendingDrawingDeletions(new Set());
    setPendingSpecsFiles([]);
    setPendingSpecsDeletions(new Set());
  };

  const handleSaveFileChanges = async (projectId: string) => {
    if (!supabase || !hasPendingFileChanges()) return;

    setIsSavingChanges(true);

    try {
      // Upload pending drawings — merge into the existing drawing PDF
      if (pendingDrawingFiles.length > 0) {
        // Step 1: Upload each new file to temporary storage
        const uploadedTempFiles: { storagePath: string; fileName: string }[] = [];

        for (const entry of pendingDrawingFiles) {
          const file = entry.file;
          console.log(`📤 Uploading temp drawing ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);

          setIsUploading(true);
          setUploadingFileName(file.name);
          setUploadProgress(0);

          const { ext } = splitFileName(file.name);
          const editedName = `${(fileNameEdits[entry.tempId] || splitFileName(file.name).base).trim() || splitFileName(file.name).base}${ext}`;
          const timestamp = Date.now();
          const filePath = `blueprints/temp_${timestamp}_${Math.random().toString(36).substring(7)}${ext}`;

          let uploadSuccess = false;

          if (shouldUseResumableUpload(file.size)) {
            console.log('📤 Using TUS resumable upload...');
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath,
              file,
              onProgress: (progress) => {
                setUploadProgress(progress.percentage);
                console.log(`   Progress: ${progress.percentage}%`);
              },
              onError: (error) => {
                console.error(`   Upload error:`, error);
                setIsUploading(false);
              }
            });
            uploadSuccess = result.success;
          } else {
            const { error: uploadError } = await supabase.storage
              .from('bucket')
              .upload(filePath, file);
            uploadSuccess = !uploadError;
          }

          setIsUploading(false);

          if (uploadSuccess) {
            uploadedTempFiles.push({ storagePath: filePath, fileName: editedName });
          }
        }

        // Step 2: Call backend to merge new pages into the existing drawing
        if (uploadedTempFiles.length > 0) {
          setIsMergingPDFs(true);
          setMergeProgress(50);

          const existingFile = blueprintFiles.find(f => !pendingDrawingDeletions.has(f.id));

          console.log(`🔄 Merging ${uploadedTempFiles.length} new file(s) into existing drawing...`);
          const mergeResponse = await fetch('/api/merge-drawing', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              estimationId: projectId,
              existingFileId: existingFile?.id || null,
              newFiles: uploadedTempFiles,
            }),
          });

          if (!mergeResponse.ok) {
            const err = await mergeResponse.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to merge drawings');
          }

          const mergeResult = await mergeResponse.json();
          console.log(`✅ Merge complete: ${mergeResult.pageCount} pages, ${(mergeResult.fileSize / 1024 / 1024).toFixed(2)} MB`);

          setMergeProgress(100);
          setIsMergingPDFs(false);
        }
      }

      // Delete drawings marked for removal
      if (pendingDrawingDeletions.size > 0) {
        const drawingsToDelete = blueprintFiles.filter((file) => pendingDrawingDeletions.has(file.id));
        for (const file of drawingsToDelete) {
          await supabase.storage.from('bucket').remove([file.storage_path]);
          await supabase.from('estimation_files').delete().eq('id', file.id);
        }
      }

      // Rename existing drawings
      for (const file of blueprintFiles) {
        if (pendingDrawingDeletions.has(file.id)) continue;
        const finalName = getEditedFileName(file.id, file.file_name, fileNameEdits);
        if (finalName !== file.file_name) {
          await supabase
            .from('estimation_files')
            .update({ file_name: finalName })
            .eq('id', file.id);
        }
      }

      // Upload pending specs
      if (pendingSpecsFiles.length > 0) {
        const { data: existingFiles } = await supabase
          .from('estimation_specs')
          .select('file_order')
          .eq('estimation_id', projectId)
          .order('file_order', { ascending: false })
          .limit(1);

        let nextOrder = existingFiles && existingFiles.length > 0 ? existingFiles[0].file_order + 1 : 0;

        for (const entry of pendingSpecsFiles) {
          const file = entry.file;
          console.log(`🔍 DEBUG: Uploading pending spec ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
          
          setIsUploading(true);
          setUploadingFileName(file.name);
          setUploadProgress(0);
          
          const { ext } = splitFileName(file.name);
          const editedName = `${(fileNameEdits[entry.tempId] || splitFileName(file.name).base).trim() || splitFileName(file.name).base}${ext}`;
          const timestamp = Date.now();
          const filePath = `specs/${timestamp}_${Math.random().toString(36).substring(7)}${ext}`;

          let uploadSuccess = false;
          
          if (shouldUseResumableUpload(file.size)) {
            console.log(`📤 Using TUS resumable upload for ${file.name}...`);
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath,
              file,
              onProgress: (progress) => {
                setUploadProgress(progress.percentage);
                console.log(`   ${file.name} progress: ${progress.percentage}%`);
              },
              onError: (error) => {
                console.error(`   Upload error:`, error);
                setIsUploading(false);
              }
            });
            uploadSuccess = result.success;
          } else {
            const { error: uploadError } = await supabase.storage
              .from('bucket')
              .upload(filePath, file);
            uploadSuccess = !uploadError;
          }
          
          setIsUploading(false);
          
          if (!uploadSuccess) continue;

          await supabase.from('estimation_specs').insert({
            estimation_id: projectId,
            file_name: editedName,
            file_size: file.size,
            file_type: file.type,
            storage_path: filePath,
            file_order: nextOrder,
          });
          nextOrder += 1;
        }
      }

      // Delete marked specs
      if (pendingSpecsDeletions.size > 0) {
        const toDelete = specsFilesForManager.filter((file) => pendingSpecsDeletions.has(file.id));
        for (const file of toDelete) {
          await supabase.storage.from('bucket').remove([file.storage_path]);
          await supabase.from('estimation_specs').delete().eq('id', file.id);
        }
      }

      // Rename existing specs
      for (const file of specsFilesForManager) {
        if (pendingSpecsDeletions.has(file.id)) continue;
        const finalName = getEditedFileName(file.id, file.file_name, fileNameEdits);
        if (finalName !== file.file_name) {
          await supabase
            .from('estimation_specs')
            .update({ file_name: finalName })
            .eq('id', file.id);
        }
      }

      const refreshed = await fetchProjectFiles(projectId);
      setBlueprintFiles(refreshed.blueprints || []);
      setSpecsFilesForManager(refreshed.specs || []);
      setProjectFilesCache((prev) => ({
        ...prev,
        [projectId]: refreshed,
      }));
      setPendingDrawingFiles([]);
      setPendingDrawingDeletions(new Set());
      setPendingSpecsFiles([]);
      setPendingSpecsDeletions(new Set());
      setFileNameEdits(buildFileNameEditMap(refreshed.blueprints || [], refreshed.specs || []));
      setIsSavingChanges(false);
      
      // Close the expanded section after successful save
      setExpandedProjectId(null);
      setBlueprintFiles([]);
      setSpecsFilesForManager([]);
      setEditingProjectNameId(null);
      setEditingProjectName('');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save file changes');
      setIsSavingChanges(false);
    }
  };
  const handleRemovePendingSpecsFile = (tempId: string) => {
    setPendingSpecsFiles((prev) => prev.filter((entry) => entry.tempId !== tempId));
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      delete updated[tempId];
      return updated;
    });
  };

  const handleToggleDeleteSpecsFile = (fileId: string) => {
    setPendingSpecsDeletions((prev) => {
      const updated = new Set(prev);
      if (updated.has(fileId)) {
        updated.delete(fileId);
      } else {
        updated.add(fileId);
      }
      return updated;
    });
  };

  const handleFileNameChange = (fileId: string, value: string) => {
    setFileNameEdits((prev) => ({
      ...prev,
      [fileId]: value,
    }));
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrawingDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDrawing(true);
  };

  const handleDrawingDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDrawing(false);
  };

  const handleDrawingDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDrawing(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    
    const uploads = Array.from(e.dataTransfer.files).map((file, index) => {
      const tempId = `pending-drawing-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingDrawingFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
        const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  const handleSpecsDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSpecs(true);
  };

  const handleSpecsDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSpecs(false);
  };

  const handleSpecsDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSpecs(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    
    const uploads = Array.from(e.dataTransfer.files).map((file, index) => {
      const tempId = `pending-spec-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingSpecsFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
        const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  const handleModalDrawingDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalDrawing(true);
  };

  const handleModalDrawingDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalDrawing(false);
  };

  const handleModalDrawingDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalDrawing(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Check for duplicates
      const existingNames = newProjectFiles.map(f => f.name.toLowerCase());
      const duplicates: string[] = [];
      const uniqueFiles = files.filter(file => {
        if (existingNames.includes(file.name.toLowerCase())) {
          duplicates.push(file.name);
          return false;
        }
        return true;
      });
      
      if (duplicates.length > 0) {
        alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
      }
      
      setNewProjectFiles(prev => [...prev, ...uniqueFiles]);
    }
  };

  const handleModalSpecsDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalSpecs(true);
  };

  const handleModalSpecsDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalSpecs(false);
  };

  const handleModalSpecsDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalSpecs(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    
    const newFiles = Array.from(e.dataTransfer.files);
    // Check for duplicates
    const existingNames = newSpecsFiles.map(f => f.name.toLowerCase());
    const duplicates: string[] = [];
    const uniqueFiles = newFiles.filter(file => {
      if (existingNames.includes(file.name.toLowerCase())) {
        duplicates.push(file.name);
        return false;
      }
      return true;
    });
    
    if (duplicates.length > 0) {
      alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
    }
    
    setNewSpecsFiles(prev => [...prev, ...uniqueFiles]);
  };

  const pendingChanges = hasPendingFileChanges();

  return (
    <div className="home-page-container">
      {/* User Menu */}
      <div className="home-user-menu">
          <div
            className={`theme-switch-home ${theme === 'dark' ? 'dark' : 'light'}`}
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            <div className="theme-switch-handle">
              {theme === 'dark' ? (
                <Moon size={10} color="#a855f7" fill="#a855f7" />
              ) : (
                <Sun size={10} color="#eab308" fill="#eab308" />
              )}
            </div>
          </div>
          <div className="user-display-card">
            <User size={16} />
            <span>{username}</span>
          </div>
          <button
            onClick={logout}
            className="logout-button-icon"
          >
            <LogOut size={18} />
          </button>
        </div>

      {/* Header */}
      <div className="home-header">
        <h1 className="home-title">Bidflow</h1>
        <p className="home-subtitle">Submit bids faster with AI-assisted electrical takeoffs</p>
          </div>

      {/* Search & Filter */}
      <div className="home-search-section">
        <div className="home-search-box">
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            className={`filter-toggle-btn ${showSortDropdown ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowSortDropdown(prev => !prev);
            }}
          >
            <SlidersHorizontal size={18} />
          </button>
          {showSortDropdown && (
            <div className="filter-dropdown" onClick={(e) => e.stopPropagation()}>
              <div className="filter-dropdown-header">Sort by</div>
              <div className="filter-dropdown-group">
                {([
                  { field: 'lastOpened' as const, label: 'Last Opened' },
                  { field: 'dateCreated' as const, label: 'Date Created' },
                  { field: 'name' as const, label: 'Name' },
                ]).map(({ field, label }) => (
                  <button
                    key={field}
                    className={`filter-option ${sortBy === field ? 'active' : ''}`}
                    onClick={() => {
                      if (sortBy === field) {
                        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy(field);
                        setSortDirection(field === 'name' ? 'asc' : 'desc');
                      }
                    }}
                  >
                    <span className="filter-option-label">{label}</span>
                    {sortBy === field && (
                      <span className="filter-option-direction">
                        {field === 'name'
                          ? (sortDirection === 'asc' ? 'A → Z' : 'Z → A')
                          : (sortDirection === 'desc' ? 'Newest first' : 'Oldest first')
                        }
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="filter-dropdown-footer">
                {displayProjects.length} project{displayProjects.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Projects Section */}
      <div className="home-projects-section">
        {/* Create Project Button */}
        <button
          className="create-project-button"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={20} />
          <span>Create New Project</span>
        </button>

        {/* Projects Container */}
        <div className="home-projects-container">
                {displayProjects.length === 0 ? (
          <div className="projects-empty-state">
            <FileText className="empty-icon" />
            <p className="empty-text">No projects found</p>
            <p className="empty-subtext">Click above to create your first project</p>
                  </div>
                ) : (
                  displayProjects.map((project) => (
                    <div
              className={`project-card-wrapper ${expandedProjectId === project.id ? 'expanded' : ''}`}
                      key={project.id}
                    >
              <div className="home-project-card">
                <div className="project-icon">
                  <FileText size={20} />
                </div>

                <div className="project-info">
                  {editingProjectNameId === project.id ? (
                            <input
                              type="text"
                      className="project-name-input"
                              value={editingProjectName}
                              onChange={(e) => setEditingProjectName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdateProjectName(project.id);
                          setEditingProjectNameId(null);
                        } else if (e.key === 'Escape') {
                          setEditingProjectNameId(null);
                                }
                              }}
                      onBlur={() => {
                        handleUpdateProjectName(project.id);
                        setEditingProjectNameId(null);
                      }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                    <div 
                      className="project-name"
                      onClick={(e) => {
                        if (expandedProjectId !== project.id) {
                          handleOpenProject(project.id);
                        } else {
                          e.stopPropagation();
                          setEditingProjectNameId(project.id);
                          setEditingProjectName(project.name);
                        }
                      }}
                    >
                      {capitalizeWords(project.name)}
                            </div>
                          )}
                  <div className="project-timestamps">
                    <span className="project-timestamp">
                      <Clock size={12} />
                      Opened {formatTimestamp(project.lastOpenedAt || project.updatedAt)}
                    </span>
                    <span className="project-timestamp-separator">·</span>
                    <span className="project-timestamp">
                      Created {formatTimestamp(project.createdAt)}
                    </span>
                  </div>
                        </div>

                          <div className="project-status-wrapper">
                            <div
                              className="project-status-badge clickable"
                              style={{
                                backgroundColor: getStatusOption(project.status).bgColor,
                                color: getStatusOption(project.status).color,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowStatusPicker(showStatusPicker === project.id ? null : project.id);
                              }}
                            >
                              {getStatusOption(project.status).label}
                              <ChevronDown size={14} style={{ marginLeft: '0.25rem', opacity: 0.7 }} />
                            </div>
                            {showStatusPicker === project.id && (
                              <div className="status-picker-dropdown">
                                {statusOptions.map((opt) => (
                                  <div
                                    key={opt.value}
                                    className={`status-picker-option ${project.status === opt.value ? 'active' : ''}`}
                                    style={{
                                      backgroundColor: project.status === opt.value ? opt.bgColor : undefined,
                                      color: project.status === opt.value ? opt.color : undefined,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (project.status !== opt.value) {
                                        updateProject(project.id, { status: opt.value });
                                      }
                                      setShowStatusPicker(null);
                                    }}
                                  >
                                    <span
                                      className="status-picker-dot"
                                      style={{ backgroundColor: opt.color }}
                                    />
                                    {opt.label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                <div className="project-actions">
                        <button
                    className={`project-expand-btn ${expandedProjectId === project.id ? 'expanded' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                      handleToggleExpand(project.id);
                          }}
                          title={expandedProjectId === project.id ? 'Collapse' : 'Edit project'}
                        >
                          <ChevronDown
                            size={18}
                            style={{
                              transform: expandedProjectId === project.id ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                            }}
                          />
                        </button>
                          <button
                    className="project-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(project.id, project.name);
                            }}
                            title="Delete project"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Expanded Edit Section */}
                      {expandedProjectId === project.id && (
                <div className="project-expanded-section" onClick={(e) => e.stopPropagation()}>
                  <div className="file-sections-grid">
                    {/* Drawings Section */}
                    <div className="file-section">
                      <div className="file-section-header">
                        <span>Drawings</span>
                        <span className="file-count">
                          {blueprintFiles.length + pendingDrawingFiles.length}
                        </span>
                      </div>

                      <div className="file-list">
                                    {blueprintFiles.length === 0 && pendingDrawingFiles.length === 0 && (
                          <div className="empty-file-state">No drawings uploaded</div>
                                    )}
                                    {blueprintFiles.map((file) => (
                                      <div
                                        key={file.id}
                            className={`file-item ${pendingDrawingDeletions.has(file.id) ? 'marked-delete' : ''}`}
                                      >
                            <FileText size={16} className="file-item-icon" />
                            <div className="file-item-details">
                              <div className="file-name-edit">
                                    <input
                                      type="text"
                                              value={fileNameEdits[file.id] ?? splitFileName(file.file_name).base}
                                              onChange={(e) => handleFileNameChange(file.id, e.target.value)}
                                              placeholder="Drawing name"
                                              disabled={isSavingChanges || pendingDrawingDeletions.has(file.id)}
                                    />
                                            <span className="file-extension">{splitFileName(file.file_name).ext}</span>
                                          </div>
                              <div className="file-size">
                                            {(file.file_size / 1024 / 1024).toFixed(2)} MB
                              </div>
                                  </div>
                                  <button
                                    type="button"
                              className={`file-remove-btn ${pendingDrawingDeletions.has(file.id) ? 'undo' : ''}`}
                                          onClick={() => handleToggleDeleteDrawingFile(file.id)}
                                    disabled={isSavingChanges}
                                          title={pendingDrawingDeletions.has(file.id) ? 'Undo remove' : 'Remove'}
                                        >
                                          {pendingDrawingDeletions.has(file.id) ? (
                                            <RotateCcw size={14} />
                                          ) : (
                                            <X size={14} />
                                          )}
                                  </button>
                                </div>
                                    ))}
                                    {pendingDrawingFiles.map((entry) => (
                          <div key={entry.tempId} className="file-item pending">
                            <FileText size={16} className="file-item-icon" />
                            <div className="file-item-details">
                              <div className="file-name-edit">
                                    <input
                                      type="text"
                                              value={fileNameEdits[entry.tempId] ?? splitFileName(entry.file.name).base}
                                              onChange={(e) => handleFileNameChange(entry.tempId, e.target.value)}
                                              placeholder="Drawing name"
                                      disabled={isSavingChanges}
                                    />
                                            <span className="file-extension">{splitFileName(entry.file.name).ext}</span>
                                          </div>
                              <div className="file-size">
                                            {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                                  </div>
                                        <button
                                          type="button"
                              className="file-remove-btn"
                                          onClick={() => handleRemovePendingDrawingFile(entry.tempId)}
                                          disabled={isSavingChanges}
                                          title="Remove pending file"
                                        >
                                          <X size={14} />
                                        </button>
                                </div>
                                    ))}
                            </div>

                      <label 
                        className={`file-upload-area ${isDraggingDrawing ? 'dragging' : ''}`}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDrawingDragEnter}
                        onDragLeave={handleDrawingDragLeave}
                        onDrop={handleDrawingDrop}
                      >
                                <input
                                  type="file"
                                        accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
                                        onChange={handleAddDrawingFiles}
                                  style={{ display: 'none' }}
                                  disabled={isSavingChanges}
                                  multiple
                                />
                        <div className="file-upload-icon">
                                  <Upload size={20} />
                                </div>
                        <div className="file-upload-text">
                          Drop files or click to upload
                                </div>
                              </label>
                                  </div>

                    {/* Specifications Section */}
                    <div className="file-section">
                      <div className="file-section-header">
                        <span>Specifications & Additional Files</span>
                        <span className="file-count">
                          {specsFilesForManager.length + pendingSpecsFiles.length}
                        </span>
                            </div>

                      <div className="file-list">
                                {specsFilesForManager.length === 0 && pendingSpecsFiles.length === 0 && (
                          <div className="empty-file-state">No specifications</div>
                                )}
                                {specsFilesForManager.map((file) => (
                                  <div
                                    key={file.id}
                            className={`file-item ${pendingSpecsDeletions.has(file.id) ? 'marked-delete' : ''}`}
                                  >
                            <FileText size={16} className="file-item-icon" />
                            <div className="file-item-details">
                              <div className="file-name-edit">
                                        <input
                                          type="text"
                                          value={fileNameEdits[file.id] ?? splitFileName(file.file_name).base}
                                          onChange={(e) => handleFileNameChange(file.id, e.target.value)}
                                          placeholder="File name"
                                          disabled={isSavingChanges}
                                        />
                                        <span className="file-extension">{splitFileName(file.file_name).ext}</span>
                                      </div>
                              <div className="file-size">
                                        {(file.file_size / 1024 / 1024).toFixed(2)} MB
                              </div>
                                    </div>
                                    <button
                                      type="button"
                              className={`file-remove-btn ${pendingSpecsDeletions.has(file.id) ? 'undo' : ''}`}
                                      onClick={() => handleToggleDeleteSpecsFile(file.id)}
                                      disabled={isSavingChanges}
                                      title={pendingSpecsDeletions.has(file.id) ? 'Undo remove' : 'Remove'}
                                    >
                                      {pendingSpecsDeletions.has(file.id) ? (
                                        <RotateCcw size={14} />
                                      ) : (
                                        <X size={14} />
                                      )}
                                    </button>
                                  </div>
                                ))}
                                {pendingSpecsFiles.map((entry) => (
                          <div key={entry.tempId} className="file-item pending">
                            <FileText size={16} className="file-item-icon" />
                            <div className="file-item-details">
                              <div className="file-name-edit">
                                        <input
                                          type="text"
                                          value={fileNameEdits[entry.tempId] ?? splitFileName(entry.file.name).base}
                                          onChange={(e) => handleFileNameChange(entry.tempId, e.target.value)}
                                          placeholder="File name"
                                          disabled={isSavingChanges}
                                        />
                                        <span className="file-extension">{splitFileName(entry.file.name).ext}</span>
                                      </div>
                              <div className="file-size">
                                        {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                                    </div>
                                    <button
                                      type="button"
                              className="file-remove-btn"
                                      onClick={() => handleRemovePendingSpecsFile(entry.tempId)}
                                      disabled={isSavingChanges}
                                      title="Remove pending file"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ))}
                                </div>

                      <label 
                        className={`file-upload-area ${isDraggingSpecs ? 'dragging' : ''}`}
                        onDragOver={handleDragOver}
                        onDragEnter={handleSpecsDragEnter}
                        onDragLeave={handleSpecsDragLeave}
                        onDrop={handleSpecsDrop}
                      >
                                      <input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.txt"
                                        onChange={handleAddSpecsFiles}
                                        style={{ display: 'none' }}
                                        disabled={isSavingChanges}
                                        multiple
                                      />
                        <div className="file-upload-icon">
                                        <Upload size={20} />
                                      </div>
                        <div className="file-upload-text">
                          Drop files or click to upload
                                      </div>
                                    </label>
                              </div>
                            </div>

                  <div className="expanded-actions">
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={handleCancelFileChanges}
                      disabled={isSavingChanges}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn-primary"
                                onClick={() => handleSaveFileChanges(project.id)}
                                disabled={!pendingChanges || isSavingChanges}
                              >
                                {isSavingChanges ? 'Saving...' : 'Save Changes'}
                              </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
                </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCancelCreate}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create New Project</h2>
                    <button
                className="modal-close-btn"
                      onClick={handleCancelCreate}
                      disabled={creatingProject}
                    >
                <X size={20} />
                    </button>
                  </div>

            <div className="modal-body">
              <div className="file-sections-grid-modal">
                <div className="form-field">
                  <label className="form-label">Project Name</label>
                      <input
                        type="text"
                    className="form-input"
                        placeholder="Enter project name..."
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        disabled={creatingProject}
                        autoFocus
                      />
                    </div>

                <div className="form-field">
                  <label className="form-label">
                    Drawings <span className="form-label-optional">(Optional)</span>
                  </label>
                  <div className="upload-hint" style={{ marginBottom: '0.75rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                    Upload multiple PDFs - they will be automatically merged into one file
                  </div>
                  {newProjectFiles.length > 0 && (
                    <div className="uploaded-files-list">
                      {newProjectFiles.map((file, index) => (
                        <div key={index} className="uploaded-file-item">
                          <FileText size={18} className="uploaded-file-icon" />
                          <div className="uploaded-file-info">
                            <div className="uploaded-file-name">{file.name}</div>
                            <div className="uploaded-file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            className="remove-uploaded-file"
                            onClick={() => removeDrawingFile(index)}
                            disabled={creatingProject}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label 
                    className={`file-upload-button ${isDraggingModalDrawing ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleModalDrawingDragEnter}
                    onDragLeave={handleModalDrawingDragLeave}
                    onDrop={handleModalDrawingDrop}
                  >
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            if (e.target.files) {
                              const files = Array.from(e.target.files);
                              const existingNames = newProjectFiles.map(f => f.name.toLowerCase());
                              const duplicates: string[] = [];
                              const uniqueFiles = files.filter(file => {
                                if (existingNames.includes(file.name.toLowerCase())) {
                                  duplicates.push(file.name);
                                  return false;
                                }
                                return true;
                              });
                              
                              if (duplicates.length > 0) {
                                alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
                              }
                              
                              setNewProjectFiles(prev => [...prev, ...uniqueFiles]);
                            }
                          }}
                          style={{ display: 'none' }}
                          disabled={creatingProject}
                          multiple
                        />
                    <div className="upload-icon-wrapper">
                          <Upload size={40} />
                        </div>
                    <div className="upload-text">
                          {newProjectFiles.length > 0 
                            ? `${newProjectFiles.length} file${newProjectFiles.length > 1 ? 's' : ''} selected - Drop or click to add more`
                            : 'Drop files or click to upload drawings'}
                        </div>
                    <div className="upload-hint">PDF files • Multiple files will be merged</div>
                      </label>
                    </div>

                <div className="form-field">
                  <label className="form-label">
                    Specifications & Additional Files <span className="form-label-optional">(Optional)</span>
                  </label>
                  <div className="upload-hint" style={{ marginBottom: '0.75rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                    Upload any project specifications, RFI responses, or additional documentation
                  </div>
                  {newSpecsFiles.length > 0 && (
                    <div className="uploaded-files-list">
                      {newSpecsFiles.map((file, index) => (
                        <div key={index} className="uploaded-file-item">
                          <FileText size={18} className="uploaded-file-icon" />
                          <div className="uploaded-file-info">
                            <div className="uploaded-file-name">{file.name}</div>
                            <div className="uploaded-file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            className="remove-uploaded-file"
                            onClick={() => removeSpecsFile(index)}
                            disabled={creatingProject}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label 
                    className={`file-upload-button ${isDraggingModalSpecs ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleModalSpecsDragEnter}
                    onDragLeave={handleModalSpecsDragLeave}
                    onDrop={handleModalSpecsDrop}
                  >
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleSpecsFileChange}
                          style={{ display: 'none' }}
                          disabled={creatingProject}
                          multiple
                        />
                    <div className="upload-icon-wrapper">
                          <Upload size={40} />
                        </div>
                    <div className="upload-text">
                          {newSpecsFiles.length > 0 
                        ? `${newSpecsFiles.length} file${newSpecsFiles.length > 1 ? 's' : ''} selected - Drop or click to add more`
                        : 'Drop files or click to upload'}
                        </div>
                    <div className="upload-hint">PDF, DOC, DOCX, TXT files</div>
                      </label>
                              </div>
                            </div>
                    </div>

            <div className="modal-footer">
              {/* Merging Progress Bar */}
              {isMergingPDFs && (
                <div style={{
                  width: '100%',
                  marginBottom: '1rem',
                  padding: '0 1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-muted)'
                  }}>
                    <span>Merging {newProjectFiles.length} PDF files...</span>
                    <span style={{ fontWeight: '600', color: 'var(--cyan-text-dark)' }}>{mergeProgress.toFixed(1)}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: 'var(--border-color)',
                    borderRadius: '9999px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: 'var(--cyan-text-dark)',
                      borderRadius: '9999px',
                      transition: 'width 0.3s ease',
                      width: `${mergeProgress}%`
                    }} />
                  </div>
                </div>
              )}
              
              {/* Upload Progress Bar */}
              {isUploading && (
                <div style={{
                  width: '100%',
                  marginBottom: '1rem',
                  padding: '0 1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-muted)'
                  }}>
                    <span>Uploading {uploadingFileName}...</span>
                    <span style={{ fontWeight: '600', color: 'var(--cyan-text-dark)' }}>{uploadProgress.toFixed(1)}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: 'var(--border-color)',
                    borderRadius: '9999px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: 'var(--cyan-text-dark)',
                      borderRadius: '9999px',
                      transition: 'width 0.3s ease',
                      width: `${uploadProgress}%`
                    }} />
                  </div>
                </div>
              )}
              
                      <button
                className="btn-secondary"
                        onClick={handleCancelCreate}
                        disabled={creatingProject}
                      >
                        Cancel
                      </button>
                      <button
                className="btn-primary"
                        onClick={handleCreateProject}
                disabled={creatingProject || !newProjectName.trim() || isMergingPDFs}
                      >
                        {isMergingPDFs ? (
                          'Merging...'
                        ) : creatingProject ? (
                  newSpecsFiles.length > 0 || newProjectFiles.length > 0
                    ? 'Uploading...' 
                            : 'Creating...'
                ) : 'Create Project'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
    </div>
  );
}

export default HomePage;
